import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useUserStore from "../stores/user";
import { isPlaceholderEmail } from "../utils/kidLogin";

// Extend the Window interface to include clarity
// @ts-ignore
declare global {
  interface Window {
    clarity?: (...args: any[]) => void;
  }
}

const CLARITY_KEY = "project_explore_cookie_clarity";

// No Clarity (session replay) and no cookie banner for K-5 picture-PIN kids: on /kid-login, and for any signed-in
// student with a hidden placeholder email. Children can't consent, and a shared classroom device keeps the consent.
function clarityBlockedFor(pathname: string, email: string | null | undefined): boolean {
  return pathname.startsWith("/kid-login") || isPlaceholderEmail(email);
}

function clarityBlockedNow(): boolean {
  return clarityBlockedFor(window.location.pathname, useUserStore.getState().user?.email);
}

function injectClarity() {
  if (window.clarity || clarityBlockedNow()) return;
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(
    window,
    document,
    "clarity",
    "script",
    import.meta.env.VITE_CLARITY_TAG_ID
  );
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [clarityEnabled, setClarityEnabled] = useState(false);
  const { pathname } = useLocation();
  const email = useUserStore((s) => s.user?.email);
  const authResolved = useUserStore((s) => s.authResolved);
  const blocked = clarityBlockedFor(pathname, email);

  // Re-checked whenever the page or the signed-in user changes. Waits until auth is known, so a restored kid
  // session never gets Clarity in the moment before the user is loaded.
  useEffect(() => {
    if (blocked) {
      setVisible(false);
      setModalOpen(false);
      return;
    }
    if (!authResolved) return;
    const clarityConsent = localStorage.getItem(CLARITY_KEY);
    if (clarityConsent === "granted") {
      injectClarity();
    }
    setVisible(!clarityConsent);
  }, [blocked, authResolved]);

  const handleAcceptAll = () => {
    localStorage.setItem(CLARITY_KEY, "granted");
    injectClarity();
    setVisible(false);
    setModalOpen(false);
  };

  const handleDenyAll = () => {
    localStorage.setItem(CLARITY_KEY, "declined");
    setVisible(false);
    setModalOpen(false);
  };

  const handleConfirmChoices = () => {
    if (clarityEnabled) {
      localStorage.setItem(CLARITY_KEY, "granted");
      injectClarity();
    } else {
      localStorage.setItem(CLARITY_KEY, "declined");
    }
    setVisible(false);
    setModalOpen(false);
  };

  if (!visible || blocked) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 w-full z-50 bg-white border-t border-gray-200 shadow flex flex-col sm:flex-row items-center justify-between px-4 py-3 gap-3">
        <span className="text-gray-700 text-sm flex-1 text-center sm:text-left">
          We use cookies to enhance your experience and analyze site usage. By
          accepting, you agree to our use of cookies for analytics. See our{" "}
          <a
            href="/privacy"
            className="text-indigo-600 underline hover:text-indigo-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          .
        </span>
        <div className="flex gap-2">
          <button
            className="btn btn-small btn-secondary"
            onClick={() => setModalOpen(true)}
          >
            Cookie Settings
          </button>
          <button className="btn btn-small" onClick={handleAcceptAll}>
            Accept All Cookies
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 p-6 relative animate-fade-in-up">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-xl font-bold"
              aria-label="Close"
              onClick={() => setModalOpen(false)}
            >
              ×
            </button>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              Your privacy preferences
            </h2>
            <div className="mb-6 border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-800">
                  Microsoft Clarity
                </span>
                <button
                  className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-200 focus:outline-none ${
                    clarityEnabled ? "bg-indigo-600" : "bg-gray-300"
                  }`}
                  onClick={() => setClarityEnabled((v) => !v)}
                  aria-pressed={clarityEnabled}
                  aria-label={
                    clarityEnabled ? "Disable Clarity" : "Enable Clarity"
                  }
                >
                  <span
                    className={`inline-block w-4 h-4 transform bg-white rounded-full shadow transition-transform duration-200 ${
                      clarityEnabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
              <p className="text-gray-600 text-sm">
                Microsoft Clarity is a web analytics tool that helps us
                understand how users interact with our website, allowing us to
                improve its performance and user experience. In order to provide
                these insights, Microsoft Clarity may store cookies on your
                device.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                By continuing to use our website, you consent to the use of
                cookies by Microsoft Clarity for the purposes mentioned above.
                If you have any concerns about your privacy or would like more
                information about how we handle data, please refer to our
                privacy policy.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-6">
              <button
                className="flex-1 btn btn-secondary"
                onClick={handleDenyAll}
              >
                Deny All
              </button>
              <button className="flex-1 btn" onClick={handleAcceptAll}>
                Allow All
              </button>
              <button
                className="flex-1 btn btn-secondary"
                onClick={handleConfirmChoices}
              >
                Confirm My Choices
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
